import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import type { Editor as TinyMCEEditor } from "tinymce";
import CodeMirror from "@uiw/react-codemirror";
import { ViewPlugin } from "@codemirror/view";
import type { EditorView } from "@codemirror/view";
import { html as htmlLang } from "@codemirror/lang-html";
import LegacyButton from "../../components/Atomos/Buttons/LegacyButton";
import { getEmailTemplateById, updateEmailTemplate } from "../../Services/EmailTemplate";
import { IEmailTemplate } from "../../interfaces/models/IEmailTemplate";
import {
  countAnchorsMissingHref,
  ensureAbsoluteImageUrls,
  getInvalidLinkHrefs,
  restoreMceHrefAttributes,
  sanitizeEmailHtml,
} from "../../utils/emailTemplateHtml";
import { uploadImage } from "../../utils/UploadImage";
import { toastSuccess, toastError } from "../../utils/toast";

const EMAIL_TEMPLATE_IMAGES_PATH = "email-templates/images/";
const TINYMCE_CDN_BASE = "https://cdnjs.cloudflare.com/ajax/libs/tinymce/6.5.0";
const TINYMCE_CONTENT_CSS_URL = `${TINYMCE_CDN_BASE}/skins/content/default/content.min.css`;
const TINYMCE_CONTENT_STYLE = "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }";
const CONTENT_CONTAINER_ID = "content-container";

const TINYMCE_MENU = {
  file: { title: "File", items: "newdocument restoredraft | preview | print" },
  edit: { title: "Edit", items: "undo redo | cut copy paste pastetext | selectall" },
  view: { title: "View", items: "code fullscreen | visualaid" },
  insert: { title: "Insert", items: "link image charmap insertdatetime media table | hr" },
  format: { title: "Format", items: "bold italic underline strikethrough | subscript superscript | blocks | align | forecolor backcolor | removeformat" },
  table: { title: "Table", items: "inserttable tableprops deletetable | tablemergecells tablesplitcells | cell row column" },
};

const TINYMCE_PLUGINS = [
  "advlist", "autolink", "lists", "link", "image", "charmap", "preview", "anchor",
  "searchreplace", "visualblocks", "code", "fullscreen", "paste", "insertdatetime",
  "media", "table", "wordcount",
];

const TINYMCE_TOOLBAR =
  "undo redo | selectall selectnone | blocks | bold italic underline | forecolor backcolor | strikethrough subscript superscript | changecase | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link unlink | image | hr | charmap | linebreak | searchreplace | code | fullscreen | removeformat";

const TINYMCE_TABLE_TOOLBAR =
  "tablemergecells tablesplitcells | tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol";

const TINYMCE_EXTENDED_VALID_ELEMENTS =
  "a[href|target|rel|name|title|style|class|id|data-mce-href|data-wsg-unsubscribe]," +
  "table[cellpadding|cellspacing|border|align|width|height|style|class|id|bgcolor|background|role|dir]," +
  "tr[align|valign|style|class|id|bgcolor|height]," +
  "td[colspan|rowspan|align|valign|width|height|style|class|id|bgcolor|background]," +
  "th[colspan|rowspan|align|valign|width|height|style|class|id]," +
  "tbody[align|valign|style|class],thead[align|valign|style|class],tfoot[align|valign|style|class]," +
  "img[src|alt|width|height|style|class|id|align|border]," +
  "font[face|size|color|style],*[style|class|id|align|valign|bgcolor|background|width|height]," +
  "meta[http-equiv|content|charset],style[type]," +
  "footer[style|class|id],header[style|class|id]";

const TINYMCE_VALID_CHILDREN =
  "+body[style|table|div|span|p],+div[table|div|span|p|a|img],+td[div|span|p|a|img|table]";

const CODE_PREVIEW_PLACEHOLDER =
  "<p style='color:#9ca3af;padding:1rem'>Escribe HTML para ver la vista previa.</p>";

const buildPreviewDocument = (content: string): string => {
  const previewContent = content.trim() || CODE_PREVIEW_PLACEHOLDER;
  const previewHead = `<meta charset="utf-8" /><link rel="stylesheet" href="${TINYMCE_CONTENT_CSS_URL}" /><style>${TINYMCE_CONTENT_STYLE}</style>`;

  if (/<html[\s>]/i.test(previewContent)) {
    if (/<head[\s>]/i.test(previewContent)) {
      return previewContent.replace(/<head([^>]*)>/i, `<head$1>${previewHead}`);
    }

    return previewContent.replace(/<html([^>]*)>/i, `<html$1><head>${previewHead}</head>`);
  }

  if (/<body[\s>]/i.test(previewContent)) {
    return `<!DOCTYPE html><html><head>${previewHead}</head>${previewContent}</html>`;
  }

  return `<!DOCTYPE html><html><head>${previewHead}</head><body>${previewContent}</body></html>`;
};

const PLACEHOLDERS: { value: string; label: string }[] = [
  { value: "{{first}}", label: "Member First Name" },
  { value: "{{middle}}", label: "Member Middle Initial" },
  { value: "{{honorific}}", label: "Member Honorific" },
  { value: "{{last}}", label: "Member Last Name" },
  { value: "{{MemberName}}", label: "Member Name (full)" },
  { value: "{{Email}}", label: "Email" },
  { value: "{{company}}", label: "Firm" },
  { value: "{{firmid}}", label: "Firm Id" },
  { value: "{{address}}", label: "Firm Address (full)" },
  { value: "{{city}}", label: "Firm City" },
  { value: "{{zip}}", label: "Firm Zip Code" },
  { value: "{{state}}", label: "Firm State" },
  { value: "{{country}}", label: "Firm Country" },
  { value: "{{complink}}", label: "Firm Link" },
  { value: "{{loglink}}", label: "Member Profile Link" },
  { value: "{{gpnmae}}", label: "Member Group Names" },
  { value: "{{gpid}}", label: "Member Group Ids" },
  {
    value: '<p style="font-size:12px;color:#666;margin-top:1em;"><a href="{{UnsubscribeUrl}}" data-wsg-unsubscribe="category" target="_blank" rel="noopener noreferrer" style="color:#666;text-decoration:underline;">Click here</a> to unsubscribe from this type of email.</p>',
    label: "Unsubscribe link (this email type)",
  },
  {
    value: '<p style="font-size:12px;color:#666;margin-top:1em;"><a href="{{UnsubscribeAllUrl}}" data-wsg-unsubscribe="all" target="_blank" rel="noopener noreferrer" style="color:#666;text-decoration:underline;">Click here</a> to unsubscribe from all WSG emails (GDPR).</p>',
    label: "Unsubscribe link (all emails / GDPR)",
  },
];

type EditorMode = "visual" | "code";

type ScrollPosition = {
  left: number;
  top: number;
};

/**
 * HTML template editor using TinyMCE (free to use – GPL/LGPL when self-hosted).
 * Loads template by id, edits HTML, saves to backend.
 */
export default function HtmlEditorPage (): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<IEmailTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [html, setHtml] = useState("");
  const [editorMode, setEditorMode] = useState<EditorMode>("visual");
  const [placeholderOpen, setPlaceholderOpen] = useState(false);
  const tinymceEditorRef = useRef<{ insertContent: (content: string) => void } | null>(null);
  const codeMirrorViewRef = useRef<EditorView | null>(null);
  const scrollRestoreFrameRef = useRef<number | null>(null);
  const captureCodeMirrorViewExtension = useMemo(
    () =>
      ViewPlugin.fromClass(
        class {
          constructor(view: EditorView) {
            codeMirrorViewRef.current = view;
          }
        }
      ),
    []
  );

  const codeMirrorExtensions = useMemo(
    () => [htmlLang(), captureCodeMirrorViewExtension],
    [captureCodeMirrorViewExtension]
  );
  const codePreviewDocument = useMemo(() => buildPreviewDocument(html), [html]);

  useEffect(() => {
    if (!id) {
      setError("Missing template id");
      return;
    }
    let cancelled = false;
    getEmailTemplateById(id)
      .then((data) => {
        if (cancelled) {
          return;
        }
        if (!data) {
          setError("Template not found");
          return;
        }
        setTemplate(data);
        setHtml(data.template?.trim() || "");
      })
      .catch(() => {
        if (!cancelled) {
          setError("Failed to load template");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const performSave = (onSuccess?: () => void) => {
    if (!template?.id) {
      return;
    }
    const editorInstance = editorMode === "visual" ? tinymceEditorRef.current : null;
    const editorWithGetContent = editorInstance as unknown as {
      getContent: () => string;
      getDoc?: () => Document;
      editorUpload?: {
        blobCache?: {
          getByUri: (u: string) => { blob: () => Blob; base64: () => string } | undefined;
        };
      };
    } | null;
    let contentToSave = editorWithGetContent
      ? editorWithGetContent.getContent()
      : html;
    // TinyMCE moves body <style> into the iframe head; prepend them so CSS is not lost.
    if (editorWithGetContent?.getDoc) {
      const headStyles = Array.from(
        editorWithGetContent.getDoc().head?.querySelectorAll("style") || [],
      )
        .map((node) => node.outerHTML)
        .join("");
      if (
        headStyles &&
        !/<style[\s>]/i.test(contentToSave)
      ) {
        contentToSave = `${headStyles}\n${contentToSave}`;
      }
    }
    contentToSave = restoreMceHrefAttributes(contentToSave);
    const blobCache = editorWithGetContent?.editorUpload?.blobCache;
    if (blobCache) {
      contentToSave = contentToSave.replace(
        /<img([^>]*)\ssrc=["'](blob:[^"']+)["']/gi,
        (match, attrs, blobUri) => {
          const blobInfo = blobCache.getByUri(blobUri);
          if (!blobInfo) {
            return match;
          }
          const dataUrl = `data:${blobInfo.blob().type};base64,${blobInfo.base64()}`;
          return `<img${attrs} src="${dataUrl}"`;
        }
      );
    }
    const missingHrefs = countAnchorsMissingHref(contentToSave);
    if (missingHrefs > 0) {
      toastError(
        `${missingHrefs} link(s) are missing href. Fix them in Code (HTML) before saving — email clients will not make those buttons clickable.`,
      );
      return;
    }
    const invalidHrefs = getInvalidLinkHrefs(contentToSave);
    if (invalidHrefs.length > 0) {
      toastError(
        `Invalid link URL(s). Fix or remove links with invalid href before saving: ${invalidHrefs.slice(0, 3).join(", ")}${invalidHrefs.length > 3 ? "…" : ""}`
      );
      return;
    }
    setSaving(true);
    const imageBaseUrl =
      process.env.REACT_APP_EMAIL_IMAGE_BASE_URL?.trim() ||
      (typeof window !== "undefined" ? window.location.origin : "");
    const sanitized = sanitizeEmailHtml(contentToSave);
    const htmlWithAbsoluteImages = ensureAbsoluteImageUrls(sanitized, imageBaseUrl);
    updateEmailTemplate({
      ...template,
      template: htmlWithAbsoluteImages,
    })
      .then(() => {
        setSaving(false);
        toastSuccess("Template saved successfully.");
        onSuccess?.();
      })
      .catch((err: unknown) => {
        setSaving(false);
        toastError(err instanceof Error ? err.message : "Failed to save template.");
      });
  };

  const handleSave = () => performSave(() => navigate(`/email-design/${template!.id}`));
  const handleSaveAndStay = () => performSave();

  const handleBack = () => {
    setShowLeaveModal(true);
  };

  const handleLeaveConfirm = () => {
    setShowLeaveModal(false);
    navigate(id ? `/email-design/${id}` : "/email-design");
  };

  const tinymceApiKey = process.env.REACT_APP_TINYMCE_API_KEY?.trim();
  const useCloud = Boolean(tinymceApiKey);
  const tinymceScriptSrc = useCloud ? undefined : `${TINYMCE_CDN_BASE}/tinymce.min.js`;

  const handleTinyMceInit = useCallback((_evt: unknown, editor: { insertContent: (content: string) => void }) => {
    tinymceEditorRef.current = editor;
  }, []);

  const getContentScrollContainer = useCallback((): HTMLElement | null => {
    return document.getElementById(CONTENT_CONTAINER_ID);
  }, []);

  const readContentScrollPosition = useCallback((): ScrollPosition | null => {
    const scrollContainer = getContentScrollContainer();
    if (!scrollContainer) {
      return null;
    }

    return {
      left: scrollContainer.scrollLeft,
      top: scrollContainer.scrollTop,
    };
  }, [getContentScrollContainer]);

  const restoreContentScrollPosition = useCallback((position: ScrollPosition | null) => {
    if (!position) {
      return;
    }

    const scrollContainer = getContentScrollContainer();
    if (!scrollContainer) {
      return;
    }

    scrollContainer.scrollTo({
      left: position.left,
      top: position.top,
      behavior: "auto",
    });
  }, [getContentScrollContainer]);

  const preserveContentScrollPosition = useCallback(() => {
    const position = readContentScrollPosition();
    if (!position) {
      return;
    }

    if (scrollRestoreFrameRef.current !== null) {
      cancelAnimationFrame(scrollRestoreFrameRef.current);
    }

    restoreContentScrollPosition(position);

    scrollRestoreFrameRef.current = window.requestAnimationFrame(() => {
      restoreContentScrollPosition(position);
      scrollRestoreFrameRef.current = window.requestAnimationFrame(() => {
        restoreContentScrollPosition(position);
        scrollRestoreFrameRef.current = null;
      });
    });
  }, [readContentScrollPosition, restoreContentScrollPosition]);

  useEffect(() => {
    return () => {
      if (scrollRestoreFrameRef.current !== null) {
        cancelAnimationFrame(scrollRestoreFrameRef.current);
      }
    };
  }, []);

  const insertPlaceholder = useCallback(
    (placeholder: string) => {
      setPlaceholderOpen(false);
      if (editorMode === "visual" && tinymceEditorRef.current) {
        tinymceEditorRef.current.insertContent(placeholder);
      } else if (editorMode === "code" && codeMirrorViewRef.current) {
        codeMirrorViewRef.current.dispatch(
          codeMirrorViewRef.current.state.replaceSelection(placeholder)
        );
        setHtml(codeMirrorViewRef.current.state.doc.toString());
      } else {
        setHtml((prev) => prev + placeholder);
      }
    },
    [editorMode]
  );

  const tinymceInit = useMemo(() => {
    const folderId = template?.id ?? "draft";
    return {
      height: 560,
      menubar: true,
      menu: TINYMCE_MENU,
      table_toolbar: TINYMCE_TABLE_TOOLBAR,
      ...(useCloud
        ? {}
        : {
          skin_url: `${TINYMCE_CDN_BASE}/skins/ui/oxide`,
          content_css: TINYMCE_CONTENT_CSS_URL,
        }),
      plugins: TINYMCE_PLUGINS,
      toolbar: TINYMCE_TOOLBAR,
      // Keep merge-tag hrefs like {{UnsubscribeAllUrl}} / {{link}} (TinyMCE otherwise strips them).
      convert_urls: false,
      allow_script_urls: true,
      urlconverter_callback: (url: string) => url,

      resize: false,
      file_picker_types: "image",
      file_picker_callback: (callback: (url: string, meta: { alt: string }) => void, _value: string, meta: { filetype?: string }) => {
        if (meta.filetype !== "image") {
          return;
        }
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.onchange = async () => {
          const file = input.files?.[0];
          if (!file) {
            return;
          }
          const url = await uploadImage(file, folderId, EMAIL_TEMPLATE_IMAGES_PATH);
          if (url) {
            callback(url, { alt: file.name.replace(/\.[^.]*$/, "") || "Image" });
          }
          else {
            toastError("Failed to upload image. Try again.");
          }
        };
        input.click();
      },
      setup: (editor: TinyMCEEditor) => {
        const shouldPreservePageScrollOnKeyDown = (event: KeyboardEvent) => {
          if (event.defaultPrevented || event.isComposing) {
            return false;
          }
          if (event.ctrlKey || event.metaKey || event.altKey) {
            return false;
          }

          return (
            event.key === "Enter" ||
            event.key === "Backspace" ||
            event.key === "Delete" ||
            event.key.length === 1
          );
        };

        editor.on("GetContent", (event) => {
          if (event.selection || !event.content) {
            return;
          }
          const format = String(event.format || "html");
          if (format !== "html" && format !== "raw") {
            return;
          }
          event.content = restoreMceHrefAttributes(event.content);
          if (/<style[\s>]/i.test(event.content)) {
            return;
          }
          const headStyles = Array.from(
            editor.getDoc()?.head?.querySelectorAll("style") || [],
          )
            .map((node) => node.outerHTML)
            .join("");
          if (headStyles) {
            event.content = `${headStyles}\n${event.content}`;
          }
        });

        editor.on("BeforeSetContent", (event) => {
          if (event.content) {
            event.content = restoreMceHrefAttributes(event.content);
          }
        });

        editor.on("keydown", (event) => {
          if (shouldPreservePageScrollOnKeyDown(event)) {
            preserveContentScrollPosition();
          }
        });

        editor.ui.registry.addButton("selectnone", {
          text: "Select none",
          tooltip: "Clear selection",
          onAction: () => editor.selection.collapse(true),
        });
        editor.ui.registry.addButton("linebreak", {
          text: "Br",
          tooltip: "Insert line break",
          onAction: () => editor.insertContent("<br>"),
        });
        const changeCase = (toUpper: boolean) => {
          const text = editor.selection.getContent({ format: "text" });
          if (!text) {
            return;
          }
          const newText = toUpper ? text.toUpperCase() : text.toLowerCase();
          editor.insertContent(editor.dom.encode(newText), { merge: false });
        };
        editor.ui.registry.addSplitButton("changecase", {
          text: "Aa",
          tooltip: "Change case",
          onAction: () => changeCase(true),
          fetch: (callback) => {
            callback([
              { type: "choiceitem" as const, text: "Uppercase", value: "upper" },
              { type: "choiceitem" as const, text: "Lowercase", value: "lower" },
            ]);
          },
          onItemAction: (_api, value) => changeCase(value === "upper"),
        });
        const body = editor.getBody();
        if (body) {
          body.addEventListener("dragover", (e: DragEvent) => {
            if (e.dataTransfer?.types.includes("Files")) {
              e.preventDefault();
            }
          }, false);
          body.addEventListener("drop", (e: DragEvent) => {
            const files = e.dataTransfer?.files;
            if (!files?.length) {
              return;
            }
            const imageFiles = Array.from(files).filter((f: File) => f.type.startsWith("image/"));
            if (imageFiles.length === 0) {
              return;
            }
            e.preventDefault();
            e.stopPropagation();
            const doc = editor.getDoc();
            const rng =
              doc.caretRangeFromPoint?.(e.clientX, e.clientY) ??
              (() => {
                const pos = (doc as Document & { caretPositionFromPoint?(x: number, y: number): { offsetNode: Node; offset: number } | null }).caretPositionFromPoint?.(e.clientX, e.clientY);
                if (!pos) {
                  return null;
                }
                const r = doc.createRange();
                r.setStart(pos.offsetNode, pos.offset);
                r.collapse(true);
                return r;
              })();
            if (rng) {
              editor.selection.setRng(rng);
            }
            const insertAtEnd = (dataUrl: string, alt: string) => {
              editor.insertContent(
                `<img src="${editor.dom.encode(dataUrl)}" alt="${editor.dom.encode(alt)}" />`,
                { merge: false }
              );
            };
            imageFiles.forEach((file: File) => {
              const reader = new FileReader();
              reader.onload = () => {
                const dataUrl = reader.result as string;
                const alt = file.name.replace(/\.[^.]*$/, "") || "Image";
                insertAtEnd(dataUrl, alt);
              };
              reader.readAsDataURL(file);
            });
          }, false);
        }
      },
      content_style: TINYMCE_CONTENT_STYLE,
      promotion: false,
      branding: false,
      browser_spellcheck: true,
      paste_as_text: false,
      paste_retain_style_properties: "all",
      paste_merge_formats: false,
      paste_data_images: true,
      paste_preprocess: (_plugin: unknown, args: { content: string }) => {
        let c = args.content || "";
        c = c.replace(/<o:[A-Za-z0-9]+[^>]*>|<\/o:[A-Za-z0-9]+>/gi, "");
        c = c.replace(/\bstyle="([^"]*)"/gi, (_m, style: string) => {
          const cleaned = style.replace(/\s*mso-[^;]+;?/gi, "").replace(/;\s*;+/g, ";").trim();
          return cleaned ? `style="${cleaned}"` : "";
        });
        args.content = c;
      },
      verify_html: false,
      extended_valid_elements: TINYMCE_EXTENDED_VALID_ELEMENTS,
      valid_children: TINYMCE_VALID_CHILDREN,
      // Keep HTML comments (TinyMCE strips them unless protected).
      // Do not protect {{merge tags}}: `protect` hides matches while editing, so
      // Allowed Merge Tags would insert as invisible mce:protected comments.
      protect: [/<!--[\s\S]*?-->/g],
      custom_elements: "style",
    };
  }, [preserveContentScrollPosition, template?.id, useCloud]);

  if (error) {
    return (
      <main className="p-6 max-w-xl">
        <p className="text-red-600 font-medium">Could not load template</p>
        <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{error}</p>
        <button type="button" onClick={() => navigate("/email-design")} className="mt-4 text-Default underline hover:no-underline">
          ← Back to templates
        </button>
      </main>
    );
  }

  return (
    <main className="p-6 bg-white" style={{ minHeight: "700px" }}>
      {showLeaveModal && (
        <div
          className="fixed inset-0 z-[1000] overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="leave-modal-title"
          onClick={() => setShowLeaveModal(false)}
        >
          <div className="fixed inset-0 bg-black bg-opacity-80" />

          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 id="leave-modal-title" className="text-xl font-decimal font-medium text-Default mb-4">
                Leave the editor?
              </h2>

              <p className="text-gray-font mb-6">
                Unsaved changes will be lost.
              </p>

              <div className="flex justify-end gap-4">
                <LegacyButton
                  text="CANCEL"
                  style="outlined"
                  color="accent"
                  content="text"
                  size="small"
                  onClick={() => setShowLeaveModal(false)}
                />
                <LegacyButton
                  text="LEAVE"
                  style="filled"
                  color="accent"
                  content="text"
                  size="small"
                  onClick={handleLeaveConfirm}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={handleBack} className="text-Default hover:underline">
            ← Back to template
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={() => setPlaceholderOpen((o) => !o)}
              aria-expanded={placeholderOpen}
              aria-haspopup="listbox"
              className="inline-flex items-center gap-2 px-3 py-2 rounded border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Allowed Merge Tags
              <span className="text-gray-500" aria-hidden>▼</span>
            </button>
            {placeholderOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  aria-hidden
                  onClick={() => setPlaceholderOpen(false)}
                />
                <ul
                  role="listbox"
                  className="absolute left-0 top-full mt-1 z-20 min-w-[12rem] py-1 rounded border border-gray-200 bg-white shadow-lg max-h-64 overflow-auto"
                >
                  {PLACEHOLDERS.map(({ value, label }) => (
                    <li key={label} role="option">
                      <button
                        type="button"
                        onClick={() => insertPlaceholder(value)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-gray-100"
                      >
                        {label}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSaveAndStay}
            disabled={saving}
            className="px-4 py-2 bg-Default text-white rounded font-medium hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="ml-2 px-4 py-2 bg-Default text-white rounded font-medium hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save template and exit"}
          </button>
        </div>
      </div>
      <div className="w-full rounded border border-gray-200 overflow-hidden" style={{ overflowAnchor: "none" }}>
        <nav className="flex border-b border-gray-200 bg-gray-50" role="tablist" aria-label="Editor mode">
          <button
            type="button"
            role="tab"
            aria-selected={editorMode === "visual"}
            onClick={() => setEditorMode("visual")}
            className={`px-4 py-2.5 text-sm font-medium ${editorMode === "visual"
              ? "bg-white text-Default border-b-2 border-Default -mb-px"
              : "text-gray-600 hover:text-gray-900"
              }`}
          >
            Visual
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={editorMode === "code"}
            onClick={() => setEditorMode("code")}
            className={`px-4 py-2.5 text-sm font-medium ${editorMode === "code"
              ? "bg-white text-Default border-b-2 border-Default -mb-px"
              : "text-gray-600 hover:text-gray-900"
              }`}
          >
            Code (HTML/CSS)
          </button>
        </nav>
        {editorMode === "visual" ? (
          <Editor
            apiKey={useCloud ? tinymceApiKey : undefined}
            tinymceScriptSrc={!useCloud ? tinymceScriptSrc : undefined}
            value={html}
            onEditorChange={(value: string) => {
              setHtml(restoreMceHrefAttributes(value));
            }}
            onInit={handleTinyMceInit}
            init={tinymceInit}
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-t-0">
            <div className="flex flex-col border-r-0 lg:border-r border-gray-200" style={{ minHeight: 520 }}>
              <p className="px-3 py-2 text-xs font-medium text-gray-500 bg-gray-50 border-b border-gray-200 shrink-0">
                HTML / CSS - Paste HTML from MJML, Adobe, or other tools without altering tags
              </p>
              <div className="flex-1 overflow-hidden" style={{ height: 488 }}>
                <CodeMirror
                  value={html}
                  height="488px"
                  extensions={codeMirrorExtensions}
                  onChange={(value: string) => setHtml(value)}
                  basicSetup={{ lineNumbers: true, foldGutter: true }}
                  className="text-left [&_.cm-editor]:outline-none [&_.cm-scroller]:overflow-auto"
                />
              </div>
            </div>
            <div className="flex flex-col bg-gray-50" style={{ minHeight: 520 }}>
              <p className="px-3 py-2 text-xs font-medium text-gray-500 bg-gray-50 border-b border-gray-200 shrink-0">
                Live Preview
              </p>
              <div className="flex-1 p-2 overflow-auto" style={{ height: 488 }}>
                <iframe
                  title="Vista previa del template"
                  srcDoc={codePreviewDocument}
                  sandbox="allow-same-origin"
                  className="w-full h-full min-h-[400px] border border-gray-200 rounded bg-white shadow-inner"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
